import React, { useEffect, useRef, useState } from "react";
import { Modal, Card, Space, Typography, Tag, Avatar, Button, Row, Col, Spin, Empty, Tabs, Grid } from "antd";
import {
  UserOutlined,
  QuestionCircleOutlined,
  MessageOutlined,
  RobotOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "./feedback.css";
import { formatDateTime } from "../../utils/formatDateTime";
import prettifyString from "../../utils/prettifyString";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { fetchFeedbackConversationAPI } from "../../services/feedback.service";
import MetadataSection from "../playground/sections/MetaDataSection";
import QueryFindingInfoButton from "../../components/QueryFindingInfoButton";

const { Text, Paragraph } = Typography;

const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const parseReplyText = (text) => {
  if (!text) return "";
  return text.split("\n").map((line, index) => (
    <span key={index}>
      {line}
      {index < text.split("\n").length - 1 && <br />}
    </span>
  ));
};

const FeedbackDetailDialog = ({ data, open, setOpen }) => {
  const AI_ACTIONS_ANIMATION_MS = 240;
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobileViewport = !screens.md;
  const isDesktopConversationLayout = Boolean(screens.md);
  const dispatch = useAppDispatch();
  const [activeDialogTab, setActiveDialogTab] = useState("currentData");
  const [conversationData, setConversationData] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationFetched, setConversationFetched] = useState(false);
  const conversationContainerRef = useRef(null);
  const aiActionsCloseTimeoutRef = useRef(null);

  const [aiActionsPanelData, setAiActionsPanelData] = useState(null);
  const [selectedAiMessageDomId, setSelectedAiMessageDomId] = useState(null);
  const [isAiActionsPanelClosing, setIsAiActionsPanelClosing] = useState(false);
  const isAiActionsPanelOpen = Boolean(aiActionsPanelData) && !isAiActionsPanelClosing;
  const shouldRenderAiActionsPanel = Boolean(aiActionsPanelData) || isAiActionsPanelClosing;
  const isAiActionsPanelVisible = shouldRenderAiActionsPanel;
  const shouldSplitPane = isDesktopConversationLayout && isAiActionsPanelVisible;
  const responsiveConversationHeight = isMobileViewport
    ? "clamp(260px, calc(100vh - 420px), 62vh)"
    : "clamp(260px, calc(100vh - 320px), 680px)";
  const bubbleMaxWidth = isMobileViewport ? "min(320px, calc(100vw - 112px))" : "400px";
  const conversationColDesktopStyle = isDesktopConversationLayout
    ? {
        flex: `0 0 ${isAiActionsPanelOpen ? "58.3333%" : "100%"}`,
        maxWidth: isAiActionsPanelOpen ? "58.3333%" : "100%",
        transition: `flex-basis ${AI_ACTIONS_ANIMATION_MS}ms ease, max-width ${AI_ACTIONS_ANIMATION_MS}ms ease`,
      }
    : undefined;
  const aiActionsColDesktopStyle = isDesktopConversationLayout
    ? {
        flex: `0 0 ${isAiActionsPanelOpen ? "41.6667%" : "0%"}`,
        maxWidth: isAiActionsPanelOpen ? "41.6667%" : "0%",
        opacity: isAiActionsPanelOpen ? 1 : 0,
        overflow: "hidden",
        transition: `flex-basis ${AI_ACTIONS_ANIMATION_MS}ms ease, max-width ${AI_ACTIONS_ANIMATION_MS}ms ease, opacity ${AI_ACTIONS_ANIMATION_MS}ms ease`,
      }
    : undefined;
  const isSelectedAiActionsMessage = (msg) =>
    Boolean(
      aiActionsPanelData &&
      (msg === aiActionsPanelData ||
        (msg?.created_at === aiActionsPanelData?.created_at &&
          msg?.content === aiActionsPanelData?.content))
    );

  const scrollToSelectedMessage = () => {
    const container = conversationContainerRef.current;
    const messageElement = selectedAiMessageDomId
      ? document.getElementById(selectedAiMessageDomId)
      : null;
    if (!container || !messageElement) return;

    const containerRect = container.getBoundingClientRect();
    const messageRect = messageElement.getBoundingClientRect();
    const offsetTopInContainer = messageRect.top - containerRect.top + container.scrollTop;
    const targetScrollTop =
      offsetTopInContainer - (container.clientHeight / 2 - messageElement.clientHeight / 2);

    container.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: "smooth",
    });
  };

  const handleJumpToSelectedMessage = () => {
    if (isDesktopConversationLayout) {
      scrollToSelectedMessage();
      return;
    }
    handleCloseAiActionsPanel();
    setTimeout(() => {
      scrollToSelectedMessage();
    }, 120);
  };

  const handleOpenAiActionsPanel = (message, messageDomId) => {
    if (aiActionsCloseTimeoutRef.current) {
      clearTimeout(aiActionsCloseTimeoutRef.current);
      aiActionsCloseTimeoutRef.current = null;
    }
    setIsAiActionsPanelClosing(false);
    setSelectedAiMessageDomId(messageDomId);
    setAiActionsPanelData(message);
  };

  const handleCloseAiActionsPanel = () => {
    if (!aiActionsPanelData) return;
    if (!isDesktopConversationLayout) {
      setAiActionsPanelData(null);
      setIsAiActionsPanelClosing(false);
      return;
    }
    setIsAiActionsPanelClosing(true);
    if (aiActionsCloseTimeoutRef.current) {
      clearTimeout(aiActionsCloseTimeoutRef.current);
    }
    aiActionsCloseTimeoutRef.current = setTimeout(() => {
      setAiActionsPanelData(null);
      setIsAiActionsPanelClosing(false);
      aiActionsCloseTimeoutRef.current = null;
    }, AI_ACTIONS_ANIMATION_MS);
  };

  useEffect(() => {
    return () => {
      if (aiActionsCloseTimeoutRef.current) {
        clearTimeout(aiActionsCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeDialogTab !== "conversation" || conversationLoading) return;

    requestAnimationFrame(() => {
      if (conversationContainerRef.current) {
        conversationContainerRef.current.scrollTop =
          conversationContainerRef.current.scrollHeight;
      }
    });
  }, [activeDialogTab, conversationLoading, conversationData]);

  if (!data) return null;

  const resolvedBy = typeof data.resolved_by === "string" ? data.resolved_by.trim() : data.resolved_by;
  const feedbackGivenBy =
    typeof data.feedback_given_by === "string" && data.feedback_given_by.trim()
      ? data.feedback_given_by.trim()
      : data.username;

  const handleFetchConversation = () => {
    if (!data?._id) return;
    setConversationLoading(true);
    setConversationFetched(false);
    dispatch(
      fetchFeedbackConversationAPI({
        evaluationId: data._id,
        finalCallback: () => {
          setConversationLoading(false);
        },
        successCallback: (response) => {
          setConversationData(response?.data || []);
          setConversationFetched(true);
        },
        failureCallback: (error) => {
          console.error("Failed to fetch conversation:", error);
          setConversationData([]);
          setConversationFetched(true);
        },
      })
    );
  };

  useEffect(() => {
    if (!open) return;
    setActiveDialogTab("currentData");
    setAiActionsPanelData(null);
    setSelectedAiMessageDomId(null);
    setIsAiActionsPanelClosing(false);
    setConversationData([]);
    setConversationFetched(false);
  }, [open, data?._id]);

  useEffect(() => {
    if (isDesktopConversationLayout) return;
    setIsAiActionsPanelClosing(false);
  }, [isDesktopConversationLayout]);

  useEffect(() => {
    if (!open || activeDialogTab !== "conversation") return;
    if (conversationFetched || conversationLoading) return;
    handleFetchConversation();
  }, [open, activeDialogTab, conversationFetched, conversationLoading]);

  const getNameColor = (name) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#1890ff",
      "#52c41a",
      "#722ed1",
      "#eb2f96",
    ];
    let hash = 0;
    if (!name) return colors[0];
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const renderConversationMessages = () => {
    if (conversationLoading) {
      return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
          <Spin size="large" tip="Loading conversation..." />
        </div>
      );
    }

    if (!conversationData || conversationData.length === 0) {
      return (
        <Empty
          description="No conversation data found"
          style={{ marginTop: "100px" }}
        />
      );
    }

    return (
      <div style={{ padding: isMobileViewport ? "12px" : "16px" }}>
        {conversationData.map((conversationPair, convIndex) => (
          <div key={convIndex}>
            {conversationPair.map((msg, msgIndex) => {
              const messageDomId = `feedback-assistant-msg-${convIndex}-${msgIndex}`;
              const messageParts =
                msg?.role === "assistant" && msg?.content
                  ? msg?.content
                    .replace(/\\n/g, "")
                    .replace(/\\n{2}/g, " \n")
                    .split(/\n{3,}/)
                    .filter((part) => part.trim())
                  : [];
              const hasAiActions =
                msg?.role === "assistant" &&
                Array.isArray(msg?.information_gathering) &&
                msg.information_gathering.length > 0;
              const queryFinding = msg?.query_finding || msg?.query_findings;

              return (
                <div
                  key={`conv-${convIndex}-msg-${msgIndex}`}
                  style={{ marginBottom: "16px" }}
                >
                  {msg?.role === "user" ? (
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Avatar
                          style={{
                            backgroundColor: getNameColor(data.username),
                            marginRight: "8px",
                            verticalAlign: "middle",
                            flexShrink: 0,
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {data.username
                            ? data.username
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)
                            : <UserOutlined />}
                        </Avatar>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {msg?.content && (
                            <div
                              style={{
                                backgroundColor: "#f0f0f0",
                                borderRadius: "8px",
                                padding: "12px",
                                maxWidth: bubbleMaxWidth,
                                display: "flex",
                                flexDirection: "column",
                                gap: msg?.ads_flag && msg?.simulated_automatic_message ? "8px" : "0px",
                              }}
                            >
                              {msg?.ads_flag && msg?.simulated_automatic_message && (
                                <div
                                  style={{
                                    backgroundColor: "rgba(0, 0, 0, 0.06)",
                                    borderLeft: "4px solid #2e8b57",
                                    padding: "6px 10px",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    lineHeight: "1.4",
                                    maxWidth: "100%",
                                    textAlign: "left",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      color: "#2e8b57",
                                      marginBottom: "2px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <iconify-icon icon="mdi:bullhorn-outline" width="14" height="14"></iconify-icon>
                                    Replied to Ad
                                  </div>
                                  <div
                                    style={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      color: "#555",
                                    }}
                                  >
                                    {msg.simulated_automatic_message}
                                  </div>
                                </div>
                              )}
                              <Paragraph style={{ marginBottom: 0 }}>
                                {msg?.content}
                              </Paragraph>
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          marginLeft: isMobileViewport ? "8px" : "40px",
                          marginTop: "4px",
                        }}
                      >
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {formatTime(msg?.created_at || new Date().toISOString())}
                        </Text>
                      </div>
                    </div>
                  ) : (
                    <div
                      id={msg?.role === "assistant" ? messageDomId : undefined}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "flex-start",
                          width: "100%",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            maxWidth: "calc(100% - 48px)",
                          }}
                        >
                          {messageParts?.length > 0 ? (
                            messageParts?.map((part, partIndex) => (
                              <div
                                key={partIndex}
                                style={{
                                  display: "flex",
                                  gap: "6px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    backgroundColor: isSelectedAiActionsMessage(msg) ? "#91caff" : "#e6f7ff",
                                    borderRadius: "8px",
                                    padding: "12px",
                                    cursor: "pointer",
                                    alignSelf: "flex-end",
                                    maxWidth: bubbleMaxWidth,
                                  }}
                                  onClick={() => {
                                    handleOpenAiActionsPanel(msg, messageDomId);
                                  }}
                                >
                                  <Paragraph style={{ marginBottom: 0 }}>
                                    {parseReplyText(part)}
                                  </Paragraph>
                                </div>
                              </div>
                            ))
                          ) : msg?.content ? (
                            <div
                              style={{
                                display: "flex",
                                gap: "6px",
                                marginBottom: "8px",
                              }}
                            >
                              <div
                                style={{
                                  backgroundColor: isSelectedAiActionsMessage(msg) ? "#91caff" : "#e6f7ff",
                                  borderRadius: "8px",
                                  padding: "12px",
                                  cursor: "pointer",
                                  alignSelf: "flex-end",
                                  maxWidth: bubbleMaxWidth,
                                }}
                                onClick={() => {
                                  handleOpenAiActionsPanel(msg, messageDomId);
                                }}
                              >
                                <Paragraph style={{ marginBottom: 0 }}>
                                  {parseReplyText(msg.content)}
                                </Paragraph>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        <Avatar
                          size="default"
                          icon={<RobotOutlined />}
                          style={{
                            backgroundColor: "#1890ff",
                            flexShrink: 0,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "4px",
                          marginRight: isMobileViewport ? "8px" : "40px",
                          marginBottom: "24px",
                        }}
                      >
                        {hasAiActions && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                              padding: "2px 8px",
                              borderRadius: "999px",
                              backgroundColor: "#e6f4ff",
                              border: "1px solid #91caff",
                              color: "#0958d9",
                              fontSize: "11px",
                              fontWeight: 500,
                              lineHeight: 1,
                            }}
                          >
                            <iconify-icon
                              icon="mdi:robot-outline"
                              width="12"
                              height="12"
                              style={{ color: "#1677ff", display: "block", flexShrink: 0 }}
                            ></iconify-icon>
                            <span>
                              {`${msg?.information_gathering?.length} AI action${msg?.information_gathering?.length > 1 ? "s" : ""}`}
                            </span>
                          </span>
                        )}
                        <QueryFindingInfoButton queryFinding={queryFinding} />
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {formatTime(msg?.created_at || new Date().toISOString())}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        width={isMobileViewport ? "calc(100vw - 16px)" : "min(1200px, calc(100vw - 32px))"}
        closeIcon={
          <Button
            type="text"
            icon={<CloseOutlined />}
            size="small"
            style={{
              color: "#8c8c8c",
              fontSize: "16px",
              border: "none",
              backgroundColor: "transparent",
            }}
            className="modal-close-btn"
          />
        }
        styles={{
          body: {
            padding: 0,
            backgroundColor: "#ffffff",
          },
          content: {
            backgroundColor: "#ffffff",
            maxHeight: isMobileViewport ? "calc(100vh - 16px)" : "calc(100vh - 48px)",
            overflowY: "auto",
          },
        }}
      >
        <Card
          bordered={false}
          title={
            <Space
              direction={isMobileViewport ? "vertical" : "horizontal"}
              style={{
                display: "flex",
                alignItems: isMobileViewport ? "flex-start" : "center",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Space align="center">
                <Avatar
                  size={isMobileViewport ? 40 : 48}
                  icon={<UserOutlined />}
                  style={{
                    backgroundColor: "#1677ff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                />
                <div>
                  <Text strong style={{ fontSize: isMobileViewport ? 18 : 20, display: "block" }}>
                    Agent Feedback
                  </Text>
                  <Text type="secondary" style={{ fontSize: isMobileViewport ? 13 : 14 }}>
                    Customer Name: {data.username}
                  </Text>
                </div>
              </Space>
              <div
                style={{
                  marginRight: isMobileViewport ? 0 : "36px",
                  width: isMobileViewport ? "100%" : "auto",
                }}
              >
                <Tabs
                  activeKey={activeDialogTab}
                  onChange={setActiveDialogTab}
                  size={isMobileViewport ? "small" : "middle"}
                  items={[
                    { key: "currentData", label: "Feedback Details" },
                    { key: "conversation", label: "View Conversation" },
                  ]}
                />
              </div>
            </Space>
          }
          style={{
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            boxShadow: "none",
          }}
          styles={{
            header: {
              borderBottom: "1px solid #f0f0f0",
              paddingBottom: isMobileViewport ? "10px" : "16px",
              backgroundColor: "#ffffff",
              paddingLeft: isMobileViewport ? "12px" : "24px",
              paddingRight: isMobileViewport ? "12px" : "24px",
              paddingTop: isMobileViewport ? "12px" : "20px",
            },
            body: {
              maxHeight: "82vh",
              overflowY: "auto",
              padding: isMobileViewport ? "12px" : "24px",
              backgroundColor: "#ffffff",
            },
          }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            

            {activeDialogTab === "currentData" ? (
              <>
                <div className="feedback-details-grid">
                  <div>
                    <Text
                      strong
                      style={{ fontSize: "14px", color: "#595959", marginBottom: "8px", display: "block" }}
                    >
                      Feedback Date
                    </Text>
                    <Space size={[8, 8]} wrap>
                      <Tag
                        color="blue"
                        style={{ fontSize: "13px" }}
                      >
                        {formatDateTime({ date: data.created_at, withTime: true })}
                      </Tag>
                    </Space>
                  </div>

                  <div>
                    <Text
                      strong
                      style={{ fontSize: "14px", color: "#595959", marginBottom: "8px", display: "block" }}
                    >
                      Feedback Given By
                    </Text>
                    <Space size={[8, 8]} wrap>
                      {feedbackGivenBy ? (
                        <Tag color="geekblue" style={{ fontSize: "13px" }}>
                          {feedbackGivenBy}
                        </Tag>
                      ) : (
                        <Text type="secondary" style={{ fontSize: "13px" }}>-</Text>
                      )}
                    </Space>
                  </div>

                  <div>
                    <Text
                      strong
                      style={{ fontSize: "14px", color: "#595959", marginBottom: "8px", display: "block" }}
                    >
                      Evaluation
                    </Text>
                    <Space size={[8, 8]} wrap>
                      <Tag color="purple" style={{ fontSize: "13px" }}>
                        {prettifyString(data.evaluation)}
                      </Tag>
                    </Space>
                  </div>

                  {data.categories && data.categories.length > 0 && (
                    <div>
                      <Text
                        strong
                        style={{ fontSize: "14px", color: "#595959", marginBottom: "8px", display: "block" }}
                      >
                        Categories
                      </Text>
                      <Space size={[8, 8]} wrap>
                        {data.categories.map((category, index) => (
                          <Tag
                            key={index}
                            color="blue"
                            style={{ fontSize: "13px" }}
                          >
                            {category}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  )}

                  <div>
                    <Text
                      strong
                      style={{ fontSize: "14px", color: "#595959", marginBottom: "8px", display: "block" }}
                    >
                      Resolve Status
                    </Text>
                    <Space size={[8, 8]} wrap>
                      <Tag color={data.resolved ? "green" : "red"} style={{ fontSize: "13px", textTransform: "capitalize" }}>
                        {data.resolved ? "Resolved" : "Unresolved"}
                      </Tag>
                    </Space>
                  </div>

                  {resolvedBy && (
                    <div>
                      <Text
                        strong
                        style={{ fontSize: "14px", color: "#595959", marginBottom: "8px", display: "block" }}
                      >
                        Resolved By
                      </Text>
                      <Space size={[8, 8]} wrap>
                        {
                          resolvedBy ? (
                            <Tag color="geekblue" style={{ fontSize: "13px" }}>
                              {resolvedBy}
                            </Tag>
                          ) : (
                            <Text type="secondary" style={{ fontSize: "13px" }}>-</Text>
                          )
                        }
                      </Space>
                    </div>
                  )}

                {
                  data.resolved && (
                    <div>
                      <Text
                        strong
                        style={{ fontSize: "14px", color: "#595959", marginBottom: "8px", display: "block" }}
                      >
                        Resolve Date
                      </Text>
                      <Space size={[8, 8]} wrap>
                        {data.resolved && (data.resolved_at || data.evaluation?.toLowerCase() === "like") ? (
                          <Tag color="blue" style={{ fontSize: "13px" }}>
                            {data.resolved_at
                              ? formatDateTime({ date: data.resolved_at, withTime: true })
                              : formatDateTime({ date: data.created_at, withTime: true })}
                          </Tag>
                        ) : <> - </>}
                      </Space>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "16px 20px",
                    borderRadius: "8px",
                  }}
                >
                  <Space style={{ marginBottom: "12px", justifyContent: "space-between", width: "100%" }}>
                    <Space>
                      <QuestionCircleOutlined
                        style={{
                          color: "#1677ff",
                          fontSize: "18px",
                        }}
                      />
                      <Text strong style={{ fontSize: "15px", color: "#262626" }}>
                        Customer Query
                      </Text>
                    </Space>
                    {/* <Space>
                      {data.language && (
                        <Tooltip title={`Query Language: ${data.language || "Unknown"}`}>
                          <Tag size="small" color="blue">
                            {data.language}
                          </Tag>
                        </Tooltip>
                      )}
                    </Space> */}
                  </Space>
                  <Paragraph style={{ margin: 0, fontSize: "14px", lineHeight: "1.6" }}>
                    {data.message || "No customer message available"}
                  </Paragraph>
                </div>

                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "16px 20px",
                    borderRadius: "8px",
                  }}
                >
                  <Space style={{ marginBottom: "12px" }}>
                    <RobotOutlined
                      style={{
                        color: "#1677ff",
                        fontSize: "18px",
                      }}
                    />
                    <Text strong style={{ fontSize: "15px", color: "#262626" }}>
                      AI Reply
                    </Text>
                  </Space>
                  <Paragraph style={{ margin: 0, fontSize: "14px", lineHeight: "1.6" }}>
                    {data.reply || "No AI reply available"}
                  </Paragraph>
                </div>

                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "16px 20px",
                    borderRadius: "8px",
                  }}
                >
                  <Space style={{ marginBottom: "12px" }}>
                    <MessageOutlined
                      style={{
                        color: "#f5222d",
                        fontSize: "18px",
                      }}
                    />
                    <Text strong style={{ fontSize: "15px", color: "#262626" }}>
                      Agent Feedback
                    </Text>
                  </Space>
                  {feedbackGivenBy && (
                    <Space
                      size={8}
                      style={{
                        marginBottom: "10px",
                      }}
                    >
                      {/* <UserOutlined style={{ color: "#1677ff" }} /> */}
                      <Text style={{ fontSize: "13px", color: "#595959", marginLeft: 12 }}>
                        by
                      </Text>
                      <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: "12px" }}>
                        {feedbackGivenBy}
                      </Tag>
                    </Space>
                  )}
                  <Paragraph
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      lineHeight: "1.6",
                      fontStyle: "italic",
                      color: "#262626",
                    }}
                  >
                    {data.remark || "No feedback provided"}
                  </Paragraph>
                </div>

                {(data.products || data.sentiment || data.personality) && (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "16px 20px",
                      borderRadius: "8px",
                    }}
                  >
                    <Text strong style={{ fontSize: "15px", color: "#262626", marginBottom: "12px", display: "block" }}>
                      Additional Information
                    </Text>
                    <Space direction="vertical" size="small" style={{ width: "100%" }}>
                      {data.products && (
                        <div>
                          <Text type="secondary" style={{ fontSize: "13px" }}>Products: </Text>
                          <Text style={{ fontSize: "13px" }}>{data.products}</Text>
                        </div>
                      )}
                      {data.sentiment && (
                        <div>
                          <Text type="secondary" style={{ fontSize: "13px" }}>Sentiment: </Text>
                          <Text style={{ fontSize: "13px" }}>{data.sentiment}</Text>
                        </div>
                      )}
                      {data.personality && (
                        <div>
                          <Text type="secondary" style={{ fontSize: "13px" }}>Personality: </Text>
                          <Text style={{ fontSize: "13px" }}>{data.personality}</Text>
                        </div>
                      )}
                    </Space>
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  overflowX: "hidden",
                }}
              >
                <Row gutter={0} wrap={!isDesktopConversationLayout} style={{ width: "100%" }}>
                  <Col xs={24} md={isAiActionsPanelVisible ? 14 : 24} lg={isAiActionsPanelVisible ? 15 : 24} style={conversationColDesktopStyle}>
                    <div
                      ref={conversationContainerRef}
                      style={{
                        height: responsiveConversationHeight,
                        overflowY: "auto",
                        backgroundColor: "#ffffff",
                        borderTop: "1px solid #f0f0f0",
                        borderRight: shouldSplitPane ? "none" : "1px solid #f0f0f0",
                        borderBottom: "1px solid #f0f0f0",
                        borderLeft: "1px solid #f0f0f0",
                        borderRadius: shouldSplitPane ? "8px 0 0 8px" : "8px",
                      }}
                    >
                      {renderConversationMessages()}
                    </div>
                  </Col>
                  {isDesktopConversationLayout && shouldRenderAiActionsPanel && (
                    <Col xs={24} md={10} lg={9} style={aiActionsColDesktopStyle}>
                      <div
                        className={isDesktopConversationLayout ? `ai-actions-drawer ${isAiActionsPanelClosing ? "is-closing" : "is-opening"}` : ""}
                        style={{
                          height: responsiveConversationHeight,
                          display: "flex",
                          flexDirection: "column",
                          backgroundColor: "#ffffff",
                          borderTop: "1px solid #f0f0f0",
                          borderRight: "1px solid #f0f0f0",
                          borderBottom: "1px solid #f0f0f0",
                          borderLeft: "1px solid #f0f0f0",
                          borderRadius: isDesktopConversationLayout ? "0 8px 8px 0" : "8px",
                          marginTop: isDesktopConversationLayout ? 0 : "8px",
                        }}
                      >
                        <Space
                          wrap
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            backgroundColor: "#ffffff",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 16px",
                            borderBottom: "1px solid #f0f0f0",
                            rowGap: "8px",
                          }}
                        >
                          <Text strong style={{ fontSize: "15px" }}>
                            AI Actions
                          </Text>
                          <Space size="small">
                            <Button
                              size="small"
                              onClick={handleJumpToSelectedMessage}
                              type="primary"
                              style={{
                                backgroundColor: "#1677ff",
                                borderColor: "#1677ff",
                              }}
                            >
                              Jump to message
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              icon={<CloseOutlined />}
                              onClick={handleCloseAiActionsPanel}
                          />
                          </Space>
                        </Space>
                        <div
                          style={{
                            padding: "16px",
                            overflowY: "auto",
                            flex: 1,
                          }}
                        >
                          <MetadataSection drawerData={aiActionsPanelData} />
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </Space>
        </Card>
      </Modal>
      <Modal
        open={!isDesktopConversationLayout && Boolean(aiActionsPanelData)}
        onCancel={handleCloseAiActionsPanel}
        footer={null}
        centered
        width="calc(100vw - 16px)"
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              paddingRight: "28px",
              gap: "8px",
            }}
          >
            <Text strong>AI Actions</Text>
            <Button size="small" onClick={handleJumpToSelectedMessage} type="primary">
              Jump to message
            </Button>
          </div>
        }
        destroyOnClose
        styles={{
          body: {
            padding: "12px",
            maxHeight: "72vh",
            overflowY: "auto",
          },
          content: {
            maxHeight: "calc(100vh - 24px)",
          },
        }}
      >
        <MetadataSection drawerData={aiActionsPanelData} />
      </Modal>
    </>
  );
};

export default FeedbackDetailDialog;
